import { useMemo } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { InputAdornment, makeStyles, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

import { Organization } from '../../../organizations/types/organization.type';
import { QuoteGlobalDiscount } from '../../types/quote.type';
import { CreateQuoteFormValues } from './CreateQuoteStepper.component';
import { OwnerSelectionTable, OwnerCandidate } from '../Forms/OwnerSelectionTable.component';

type Props = {
  organization: Organization;
  control: Control<CreateQuoteFormValues>;
  watch: UseFormWatch<CreateQuoteFormValues>;
  setValue: UseFormSetValue<CreateQuoteFormValues>;
};

const CONDITIONS_MAX_LENGTH = 2000;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  columns: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'stretch',
  },
  leftColumn: {
    flexBasis: '40%',
    flexGrow: 0,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  rightColumn: {
    flexBasis: '60%',
    flexGrow: 0,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    minHeight: 0,
    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  fieldsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  flexRow: {
    display: 'flex',
    gap: '1rem',
    '& > *': {
      flex: 1,
    },
  },
  discountRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  toggleGroup: {
    height: 40,
  },
  valueField: {
    flex: 1,
  },
  growingTextField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiInputBase-root': {
      flex: 1,
      alignItems: 'stretch',
    },
    '& .MuiInputBase-inputMultiline': {
      flex: 1,
      height: '100%',
    },
  },
  counter: {
    alignSelf: 'flex-end',
    fontSize: 12,
    opacity: 0.7,
  },
});

function isPercentDiscount(reduction: QuoteGlobalDiscount['reduction'] | undefined): reduction is 'percent' {
  return reduction === 'percent';
}

export function ConfigurationStep({ organization, control, watch, setValue }: Props) {
  const classes = useStyles();
  const globalDiscount = watch('globalDiscount');
  const conditions = watch('conditions') ?? '';

  const candidates: OwnerCandidate[] = useMemo(
    () => [
      {
        id: organization.contact.id,
        firstname: organization.contact.firstname,
        lastname: organization.contact.lastname,
        email: organization.contact.email,
        role: 'contact',
      },
      ...organization.members.map((member) => ({
        id: member.id,
        firstname: member.firstname,
        lastname: member.lastname,
        email: member.email,
        role: 'member' as const,
      })),
    ],
    [organization]
  );

  const handleReductionChange = (
    _event: unknown,
    value: QuoteGlobalDiscount['reduction'] | null
  ) => {
    if (!value) return;
    setValue('globalDiscount.reduction', value, { shouldDirty: true });
  };

  return (
    <div className={classes.root}>
      <div className={classes.columns}>
        <div className={classes.leftColumn}>
          <section className={classes.section}>
            <h4>Livraison</h4>
            <Paper className={classes.fieldsGroup}>
              <div className={classes.flexRow}>
                <Controller
                  name="delivery.date"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Date de livraison"
                      type="date"
                      variant="outlined"
                      size="small"
                      required
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                    />
                  )}
                />
                <Controller
                  name="delivery.schedule"
                  control={control}
                  rules={{ required: true, validate: (value) => (typeof value === 'string' ? value.trim().length > 0 : false) }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Créneau horaire"
                      placeholder="08H00-18H00"
                      variant="outlined"
                      size="small"
                      required
                      fullWidth
                      error={!!error}
                    />
                  )}
                />
              </div>
              <Controller
                name="delivery.deliveryPointId"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Point de livraison"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    error={!!error}
                    helperText={error ? 'Sélectionnez un point de livraison' : undefined}
                  >
                    {organization.deliveryPoints.map((deliveryPoint) => (
                      <MenuItem key={deliveryPoint.id} value={deliveryPoint.id}>
                        {deliveryPoint.name} — {deliveryPoint.address.street}, {deliveryPoint.address.city}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Paper>
          </section>

          <section className={classes.section}>
            <h4>Remise globale</h4>
            <Paper className={classes.fieldsGroup}>
              <div className={classes.discountRow}>
                <Controller
                  name="globalDiscount.value"
                  control={control}
                  rules={{ min: 0 }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      className={classes.valueField}
                      label="Valeur de la remise"
                      type="number"
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={field.value ?? 0}
                      onChange={(event) => field.onChange(Number(event.target.value))}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {isPercentDiscount(globalDiscount?.reduction) ? '%' : '€'}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <ToggleButtonGroup
                  className={classes.toggleGroup}
                  exclusive
                  size="small"
                  value={globalDiscount?.reduction ?? 'percent'}
                  onChange={handleReductionChange}
                >
                  <ToggleButton value="percent">%</ToggleButton>
                  <ToggleButton value="flat">€</ToggleButton>
                </ToggleButtonGroup>
              </div>
            </Paper>
          </section>

          <section className={classes.section}>
            <h4>Validité</h4>
            <Paper className={classes.fieldsGroup}>
              <Controller
                name="validUntil"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Valable jusqu'au"
                    type="date"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!error}
                  />
                )}
              />
              <Controller
                name="conditions"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    className={classes.growingTextField}
                    label="Conditions particulières"
                    placeholder="Modalités de paiement, conditions de livraison, etc."
                    variant="outlined"
                    size="small"
                    multiline
                    minRows={4}
                    fullWidth
                    inputProps={{ maxLength: CONDITIONS_MAX_LENGTH }}
                  />
                )}
              />
              <Typography className={classes.counter}>
                {conditions.length} / {CONDITIONS_MAX_LENGTH}
              </Typography>
            </Paper>
          </section>
        </div>

        <div className={classes.rightColumn}>
          <h4>Propriétaire</h4>
          <Controller
            name="ownerId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <OwnerSelectionTable
                candidates={candidates}
                selectedOwnerId={field.value}
                onSelect={field.onChange}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
